import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";
import { CONTACT } from "../../data/content";
import { IMAGES } from "../../data/content";

const LINE_TRANSITION = { duration: 1, ease: [0.22, 1, 0.36, 1] };

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ ...LINE_TRANSITION, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0, 0.35]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-pblv-bg pt-28"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 pb-16 md:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-24">
        {/* Left: type block */}
        <div className="relative z-20 lg:col-span-7 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-7 inline-flex max-w-xl items-center gap-2 rounded-2xl border border-pblv-ink/10 bg-white/70 px-4 py-2.5 text-[10px] font-semibold uppercase leading-relaxed tracking-[0.16em] text-pblv-inksoft backdrop-blur md:text-xs"
          >
            <MapPin className="h-3.5 w-3.5 shrink-0 text-pblv-mauve" strokeWidth={2} />
            <span>Réseau de répit & ressourcement · Femmes de plus de quarante ans · Guadeloupe</span>
          </motion.div>

          <h1 className="font-serif text-5xl font-medium leading-[0.95] tracking-tight text-pblv-ink md:text-7xl lg:text-[5.6rem]">
            <MaskedLine delay={0.15}>Répit,</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="italic text-pblv-mauve">reconnexion,</span>
            </MaskedLine>
            <MaskedLine delay={0.41}>sororité.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-pblv-inksoft md:text-xl"
          >
            « Parfois, il suffit d'une T'POZ pour retrouver le chemin de
            soi. » Un réseau solidaire de répit et de ressourcement, près
            de chez vous.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#parentheses"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-pblv-mauve px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-pblv-mauvedark hover:shadow-[0_16px_40px_rgba(156,128,155,0.35)]"
            >
              Découvrir T'POZ
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#reseau"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-pblv-ink/15 bg-transparent px-7 py-4 text-sm font-semibold text-pblv-ink transition-all duration-300 hover:-translate-y-1 hover:border-pblv-ink/40 hover:bg-white"
            >
              Rejoindre le réseau
            </a>
          </motion.div>
        </div>

        {/* Right: parallax image */}
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative h-[380px] overflow-hidden rounded-[2rem] md:h-[520px] lg:h-[640px] lg:rounded-[3rem]"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Femme afro-caribéenne sereine dans la nature, en Guadeloupe"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-[120%] w-full object-cover"
            />
            <motion.div
              style={{ opacity: overlayOpacity }}
              className="absolute inset-0 bg-gradient-to-t from-pblv-ink/50 to-transparent"
            />
          </motion.div>

          {/* floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="absolute -left-2 bottom-6 z-20 rounded-2xl border border-white/60 bg-white/85 p-5 shadow-[0_12px_40px_rgba(45,40,48,0.12)] backdrop-blur-xl md:-left-10"
          >
            <p className="font-serif text-4xl font-semibold text-pblv-mauve">
              3
            </p>
            <p className="mt-1 text-xs font-medium leading-snug text-pblv-inksoft">
              T'POZ offertes
              <br />
              par an et par femme
            </p>
          </motion.div>
        </div>
      </div>

      {/* bottom hint */}
      <motion.a
        href="#constat"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-6 left-1/2 z-20 hidden -translate-x-1/2 items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-pblv-inksoft lg:flex"
        data-testid="hero-scroll-hint"
      >
        <span className="h-10 w-px animate-pulse bg-pblv-ink/30" />
        Faire défiler
      </motion.a>
    </section>
  );
};

export default Hero;
